/* eslint-disable @typescript-eslint/ban-ts-comment */
import { autobind } from "core-decorators";
import BaseController from "@/components/mapbox/Lib/Controllers/BaseController";
import MapboxViewer from "@/components/mapbox/MapboxViewer";
import * as mapboxgl from "mapbox-gl";
import { Point } from "mapbox-gl";
import useDrawingStore from "@/stores/useDrawingStore";
import { DrawingTool } from "@/types/enum";

class DrawingOffsetController extends BaseController {
  public static name = "DrawingOffsetController";
  public static event = {
    COMPLETE: "complete",
  } as const;

  private _positions: [number, number][] = [];
  private _tempSourceId: string | null = null;
  private _tempFillLayerId: string | null = null;
  private _tempLineLayerId: string | null = null;
  private _lastPosition: [number, number] | null = null;

  constructor(mapboxViewer: MapboxViewer) {
    super(mapboxViewer);
    this._mapboxViewer = mapboxViewer;
  }

  public set enabled(value: boolean) {
    this._enabled = value;
    const canvas = this._mapboxViewer._mapViewer.getCanvas();
    if (value) {
      canvas.style.cursor = "crosshair";
      canvas.addEventListener("pointerdown", this.onPointerDownController);
      canvas.addEventListener("pointermove", this.onPointerMoveController);
      canvas.addEventListener("dblclick", this.handleDoubleClick);
      window.document.addEventListener("keydown", this.handleKeyDown);
    } else {
      this.dispose();
    }
  }

  public get enabled() {
    return this._enabled;
  }

  @autobind
  protected override handleClick(event: PointerEvent): boolean | void {
    event.preventDefault();

    if (event.button !== 0 && event.which !== 1) {
      return true;
    }

    if (
      this._onDownPosition.x === this._onUpPosition.x &&
      this._onDownPosition.y === this._onUpPosition.y
    ) {
      const canvas = this._mapboxViewer._mapViewer.getCanvas();
      const rect = canvas.getBoundingClientRect();
      const point = new Point(
        event.clientX - rect.left,
        event.clientY - rect.top
      );
      const lngLat = this._mapboxViewer._mapViewer.unproject(point);
      const pickedPosition: [number, number] = [lngLat.lng, lngLat.lat];

      if (
        this._positions.length > 2 &&
        this._arePositionsEqual(this._positions[0], pickedPosition)
      ) {
        this.complete();
        return;
      }

      if (
        this._lastPosition &&
        this._arePositionsEqual(this._lastPosition, pickedPosition) &&
        this._positions.length > 2
      ) {
        this.complete();
        return;
      }

      this._positions.push(pickedPosition);
      this._lastPosition = [...pickedPosition] as [number, number];
    }
  }

  @autobind
  handleDoubleClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (this._positions.length > 2) {
      this.complete();
    }
  }

  @autobind
  protected override onPointerMoveController(event: PointerEvent) {
    const canvas = this._mapboxViewer._mapViewer.getCanvas();
    const rect = canvas.getBoundingClientRect();
    const point = new Point(
      event.clientX - rect.left,
      event.clientY - rect.top
    );
    const lngLat = this._mapboxViewer._mapViewer.unproject(point);
    const currentCoord: [number, number] = [lngLat.lng, lngLat.lat];

    if (this._positions.length < 1) {
      return;
    }

    const polygonCoords: [number, number][] =
      this._positions.length === 1
        ? [...this._positions, currentCoord]
        : [...this._positions, currentCoord, this._positions[0]];

    canvas.style.cursor = "crosshair";

    if (!this._tempSourceId) {
      this._tempSourceId = "temp-offset-source";
      this._tempFillLayerId = "temp-offset-fill-layer";
      this._tempLineLayerId = "temp-offset-line-layer";

      this._mapboxViewer._mapViewer.addSource(this._tempSourceId, {
        type: "geojson",
        data: {
          type: "Feature",
          geometry:
            this._positions.length < 2
              ? { type: "LineString", coordinates: polygonCoords }
              : { type: "Polygon", coordinates: [polygonCoords] },
          properties: {},
        },
      });

      if (this._positions.length < 2) {
        this._mapboxViewer._mapViewer.addLayer({
          id: this._tempLineLayerId,
          type: "line",
          source: this._tempSourceId,
          layout: {},
          paint: {
            "line-color": "#f59e0b",
            "line-width": 2,
            "line-dasharray": [2, 1],
          },
        });
      } else {
        this._mapboxViewer._mapViewer.addLayer({
          id: this._tempFillLayerId,
          type: "fill",
          source: this._tempSourceId,
          layout: {},
          paint: {
            "fill-color": "#f59e0b",
            "fill-opacity": 0.2,
          },
        });

        this._mapboxViewer._mapViewer.addLayer({
          id: this._tempLineLayerId,
          type: "line",
          source: this._tempSourceId,
          layout: {},
          paint: {
            "line-color": "#f59e0b",
            "line-width": 2,
            "line-dasharray": [2, 1],
          },
        });
      }
    } else {
      const source = this._mapboxViewer._mapViewer.getSource(
        this._tempSourceId
      ) as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({
          type: "Feature",
          geometry:
            this._positions.length < 2
              ? { type: "LineString", coordinates: polygonCoords }
              : { type: "Polygon", coordinates: [polygonCoords] },
          properties: {},
        });
      }
    }
  }

  @autobind
  handleKeyDown(event: KeyboardEvent) {
    if (!this._enabled) {
      return;
    }
    if (event.key === "Escape") {
      this.reset();
    } else if (event.key === "Enter") {
      if (this._positions.length > 2) {
        this.complete();
      }
    }
  }

  @autobind
  complete() {
    if (!this._enabled) {
      return;
    }
    if (this._positions.length > 2) {
      const closedCoordinates = [...this._positions, this._positions[0]];

      const { offsetTargetFeatureId } = useDrawingStore.getState();
      //@ts-ignore
      this.dispatchEvent({
        type: DrawingOffsetController.event.COMPLETE,
        content: {
          offsetTargetFeatureId,
          offsetCoordinates: closedCoordinates,
        },
      });

      this.reset();
      useDrawingStore.getState().setActiveTool(DrawingTool.NONE);
    }
  }

  @autobind
  reset() {
    if (!this.enabled) {
      return;
    }
    this._removeTempLayer();
    this._positions = [];
    this._tempSourceId = null;
    this._tempFillLayerId = null;
    this._tempLineLayerId = null;
    this._lastPosition = null;
  }

  @autobind
  dispose() {
    super.dispose();
    const canvas = this._mapboxViewer._mapViewer.getCanvas();
    if (canvas) {
      canvas.style.cursor = "default";
      canvas.removeEventListener("pointerdown", this.onPointerDownController);
      canvas.removeEventListener("pointermove", this.onPointerMoveController);
      canvas.removeEventListener("dblclick", this.handleDoubleClick);
      window.document.removeEventListener("keydown", this.handleKeyDown);
    }
    this._removeTempLayer();
    this._positions = [];
    this._tempSourceId = null;
    this._tempFillLayerId = null;
    this._tempLineLayerId = null;
    this._lastPosition = null;
  }

  private _removeTempLayer() {
    if (
      this._tempFillLayerId &&
      this._mapboxViewer._mapViewer.getLayer(this._tempFillLayerId)
    ) {
      this._mapboxViewer._mapViewer.removeLayer(this._tempFillLayerId);
    }
    if (
      this._tempLineLayerId &&
      this._mapboxViewer._mapViewer.getLayer(this._tempLineLayerId)
    ) {
      this._mapboxViewer._mapViewer.removeLayer(this._tempLineLayerId);
    }
    if (
      this._tempSourceId &&
      this._mapboxViewer._mapViewer.getSource(this._tempSourceId)
    ) {
      this._mapboxViewer._mapViewer.removeSource(this._tempSourceId);
    }
  }

  private _arePositionsEqual(
    pos1: [number, number],
    pos2: [number, number]
  ): boolean {
    const tolerance = 1e-6;
    return (
      Math.abs(pos1[0] - pos2[0]) < tolerance &&
      Math.abs(pos1[1] - pos2[1]) < tolerance
    );
  }
}

export default DrawingOffsetController;
